import type { TranscriptEntry } from "../types.ts";

export type ConversationSegmentKind = "answer" | "exchange";

export interface ConversationSegment {
  speaker: string;
  speakerKey: string | null;
  speakerIdentity: string;
  segmentKind: ConversationSegmentKind;
  exchangeGroup: number;
  startTs: number;
  endTs: number;
  durationMs: number;
  wordCount: number;
  entries: TranscriptEntry[];
  isClosed: boolean;
  isStale: boolean;
}

export function buildConversationSegments(
  transcript: TranscriptEntry[],
  completionPauseMs: number,
  now: number,
): ConversationSegment[] {
  const finals = transcript
    .map((entry, inputOrdinal) => ({ entry, inputOrdinal }))
    .filter(({ entry }) => entry.final && entry.text.trim())
    .sort((left, right) => left.entry.ts - right.entry.ts || left.inputOrdinal - right.inputOrdinal)
    .map(({ entry }) => entry);

  if (finals.length === 0) return [];

  const blocks: ConversationSegment[] = [];
  let current: TranscriptEntry[] = [finals[0]];
  let exchangeGroup = 0;

  for (let index = 1; index < finals.length; index += 1) {
    const previous = finals[index - 1];
    const next = finals[index];
    const gapMs = Math.max(0, next.ts - previous.ts);
    const sameIdentity = speakerIdentity(next) === speakerIdentity(previous);
    const pauseBreak = gapMs > completionPauseMs;

    if (sameIdentity && !pauseBreak) {
      current.push(next);
      continue;
    }

    const endTs = pauseBreak ? previous.ts + completionPauseMs : next.ts;
    blocks.push(toConversationSegment(current, {
      endTs,
      exchangeGroup,
      isClosed: true,
      isStale: false,
    }));
    if (pauseBreak) exchangeGroup += 1;
    current = [next];
  }

  const latestTs = current.at(-1)!.ts;
  const isStale = now - latestTs > completionPauseMs;
  const endTs = isStale ? latestTs + completionPauseMs : Math.max(now, latestTs);
  blocks.push(toConversationSegment(current, {
    endTs,
    exchangeGroup,
    isClosed: isStale,
    isStale,
  }));
  return blocks;
}

export function mergeConversationSegments(
  segments: ConversationSegment[],
): ConversationSegment {
  if (segments.length === 0) throw new Error("at least one conversation segment is required");
  const entries = segments.flatMap((segment) => segment.entries);
  const first = segments[0];
  const last = segments.at(-1)!;
  return {
    speaker: last.speaker,
    speakerKey: last.speakerKey,
    speakerIdentity: last.speakerIdentity,
    segmentKind: segments.length > 1 ? "exchange" : last.segmentKind,
    exchangeGroup: last.exchangeGroup,
    startTs: first.startTs,
    endTs: last.endTs,
    durationMs: Math.max(0, last.endTs - first.startTs),
    wordCount: countTranscriptWords(entries),
    entries,
    isClosed: last.isClosed,
    isStale: last.isStale,
  };
}

export function speakerIdentity(entry: Pick<TranscriptEntry, "speaker" | "speakerKey">): string {
  return entry.speakerKey?.trim() || entry.speaker.trim();
}

export function countTranscriptWords(entries: TranscriptEntry[]): number {
  return entries
    .map((entry) => entry.text.trim())
    .join(" ")
    .split(/\s+/)
    .filter(Boolean).length;
}

function toConversationSegment(
  entries: TranscriptEntry[],
  input: {
    endTs: number;
    exchangeGroup: number;
    isClosed: boolean;
    isStale: boolean;
  },
): ConversationSegment {
  const first = entries[0];
  const startTs = first.ts;
  return {
    speaker: first.speaker,
    speakerKey: first.speakerKey?.trim() || null,
    speakerIdentity: speakerIdentity(first),
    segmentKind: "answer",
    exchangeGroup: input.exchangeGroup,
    startTs,
    endTs: Math.max(startTs, input.endTs),
    durationMs: Math.max(0, input.endTs - startTs),
    wordCount: countTranscriptWords(entries),
    entries,
    isClosed: input.isClosed,
    isStale: input.isStale,
  };
}
